package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document

/**
 * Self-contained premium styling for the Team (Команда) screen. Same approach as
 * [InjectSubscriptionStyles]: injected once, plain CSS so we can do things the
 * Compose modifier layer can't (dot-motif hero, overlapping avatar stack,
 * role-tinted cards via a per-element --rc variable + color-mix). Mirrors the
 * subscription aesthetic — Unbounded display font, emerald brand, warm surfaces.
 */
@Composable
internal fun InjectTeamStyles() {
    LaunchedEffect(Unit) {
        if (document.getElementById("qk-sub-fonts") == null && document.getElementById("qk-tm-fonts") == null) {
            document.createElement("link").also {
                it.setAttribute("id", "qk-tm-fonts")
                it.setAttribute("rel", "stylesheet")
                it.setAttribute(
                    "href",
                    "https://fonts.googleapis.com/css2?family=Unbounded:wght@600;700;800&display=swap",
                )
                document.head?.appendChild(it)
            }
        }
        val style = document.getElementById("qk-tm-styles") ?: document.createElement("style").also {
            it.setAttribute("id", "qk-tm-styles")
            document.head?.appendChild(it)
        }
        style.textContent = TEAM_CSS
    }
}

// language=CSS
private const val TEAM_CSS = """
.qk-tm{--e:#0F9D6E;--ed:#0B7A55;width:100%}
.qk-tm-disp{font-family:'Unbounded',system-ui,sans-serif;letter-spacing:-.02em}

/* ---- hero ---- */
.qk-tm-hero{position:relative;overflow:hidden;border-radius:22px;padding:20px 24px 24px;margin-bottom:16px;color:#fff;
  background:linear-gradient(135deg,#12A574 0%,#0B7A55 58%,#0A6647 100%);
  box-shadow:0 22px 50px -22px rgba(11,122,85,.7)}
.qk-tm-hero::after{content:'';position:absolute;top:-30px;right:-24px;width:240px;height:190px;pointer-events:none;opacity:.5;
  background-image:radial-gradient(rgba(255,255,255,.5) 2px,transparent 2.4px);background-size:14px 14px;
  -webkit-mask-image:linear-gradient(215deg,#000,transparent 76%);mask-image:linear-gradient(215deg,#000,transparent 76%)}
.qk-tm-top{position:relative;z-index:2;display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px}
.qk-tm-top .over{font-size:16px;font-weight:800;letter-spacing:.01em;color:#fff}
.qk-tm-main{position:relative;z-index:1;display:flex;align-items:center;justify-content:space-between;gap:20px}
.qk-tm-hl{min-width:0}
.qk-tm-hero .cr{display:flex;align-items:baseline;gap:10px}
.qk-tm-hero .num{font-size:46px;font-weight:700;line-height:.9}
.qk-tm-hero .word{font-size:14px;font-weight:600;color:rgba(255,255,255,.85)}
.qk-tm-hero .sub{display:block;font-size:12.5px;color:rgba(255,255,255,.8);margin-top:9px;max-width:340px;line-height:1.45}

.qk-tm-stack{position:relative;z-index:1;display:flex;flex:none;padding-left:14px}
.qk-tm-stack .a{width:48px;height:48px;border-radius:50%;margin-left:-14px;border:2.5px solid rgba(255,255,255,.92);
  display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;color:#fff;
  background-size:cover;background-position:center;box-shadow:0 5px 12px rgba(0,0,0,.22)}
.qk-tm-stack .a.more{background:rgba(255,255,255,.2);color:#fff;font-size:13px;-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px)}

/* ---- generic panel ---- */
.qk-tm-card{background:#fff;border:1px solid #ECE8E2;border-radius:20px;padding:20px;margin-bottom:16px;
  box-shadow:0 2px 10px rgba(31,36,33,.04)}
.qk-tm-card .cap{font-size:16px;font-weight:800;color:#14201B}
.qk-tm-card .hint{font-size:12.5px;color:#6B7280;margin-top:5px;line-height:1.45}

/* ---- role selector ---- */
.qk-tm-roles{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin:16px 0 4px}
@media(max-width:600px){.qk-tm-roles{grid-template-columns:1fr}}
.qk-tm-role{position:relative;border:1.5px solid #ECE8E2;border-radius:15px;padding:13px 34px 13px 13px;cursor:pointer;
  display:flex;align-items:center;gap:11px;background:#fff;
  transition:border-color .2s,background .2s,box-shadow .25s,transform .2s}
.qk-tm-role:hover{transform:translateY(-2px);box-shadow:0 14px 28px -18px rgba(31,36,33,.4)}
.qk-tm-role.sel{border-color:var(--rc);background:linear-gradient(180deg,color-mix(in srgb,var(--rc) 9%,#fff),#fff);
  box-shadow:0 14px 30px -18px var(--rc)}
.qk-tm-role .ic{width:34px;height:34px;border-radius:11px;flex:none;display:flex;align-items:center;justify-content:center;
  background:color-mix(in srgb,var(--rc) 14%,#fff);color:var(--rc)}
.qk-tm-role .rt{min-width:0}
.qk-tm-role .rn{display:block;font-size:13.5px;font-weight:700;color:#14201B}
.qk-tm-role .rp{display:block;font-size:11px;color:#6B7280;margin-top:1px}
.qk-tm-role .rd{position:absolute;top:11px;right:11px;width:15px;height:15px;border-radius:50%;border:2px solid #D7D2CA;transition:.2s}
.qk-tm-role.sel .rd{border-color:var(--rc);background:var(--rc);box-shadow:inset 0 0 0 2.5px #fff}

.qk-tm-btn{width:100%;height:48px;border:none;border-radius:13px;cursor:pointer;color:#fff;font-size:14.5px;font-weight:700;
  font-family:inherit;display:inline-flex;align-items:center;justify-content:center;gap:8px;margin-top:14px;
  background:linear-gradient(135deg,#0F9D6E,#0B7A55);box-shadow:0 12px 26px -12px rgba(15,157,110,.6);
  transition:filter .2s,box-shadow .25s,transform .15s}
.qk-tm-btn:hover{filter:brightness(1.05)}
.qk-tm-btn:active{transform:scale(.985)}
.qk-tm-btn.pro{background:linear-gradient(135deg,#C98A2B,#A96F1B);box-shadow:0 12px 26px -12px rgba(201,138,43,.6)}
.qk-tm-propill{background:rgba(255,255,255,.22);border-radius:999px;padding:2px 9px;font-size:11px;font-weight:800;letter-spacing:.4px}

.qk-tm-link{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:12px;
  background:rgba(15,157,110,.07);border:1px dashed rgba(15,157,110,.42);border-radius:12px;padding:11px 13px;
  animation:qk-fade-in .25s ease both}
.qk-tm-link .u{font-size:12px;color:#0B7A55;word-break:break-all;font-weight:500;line-height:1.4}
.qk-tm-link .cp{display:inline-flex;align-items:center;gap:5px;flex:none;cursor:pointer;color:#0F9D6E;font-size:12px;font-weight:700}

/* ---- roster ---- */
.qk-tm-sec{font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#9CA3AF;margin:2px 0 10px}
.qk-tm-list{display:flex;flex-direction:column;gap:8px}
.qk-tm-mem{display:flex;align-items:center;gap:12px;background:#fff;border:1px solid #F1EDE7;border-radius:15px;padding:11px 13px;
  transition:box-shadow .2s,transform .2s,border-color .2s}
.qk-tm-mem:hover{transform:translateY(-1px);box-shadow:0 12px 26px -18px rgba(31,36,33,.4);border-color:#ECE8E2}
.qk-tm-mem.owner{background:linear-gradient(180deg,rgba(15,157,110,.06),#fff);border-color:rgba(15,157,110,.25)}
.qk-tm-av{width:44px;height:44px;border-radius:50%;flex:none;display:flex;align-items:center;justify-content:center;
  font-size:16px;font-weight:700;color:#fff;background-size:cover;background-position:center;box-shadow:0 3px 8px rgba(0,0,0,.12)}
.qk-tm-mi{flex:1;min-width:0}
.qk-tm-nm{font-size:14px;font-weight:700;color:#14201B;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.qk-tm-meta{font-size:11.5px;color:#9CA3AF;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.qk-tm-right{display:flex;align-items:center;gap:8px;flex:none}
.qk-tm-chip{font-size:11px;font-weight:800;letter-spacing:.02em;padding:4px 10px;border-radius:999px;
  color:var(--rc);background:color-mix(in srgb,var(--rc) 13%,#fff)}
.qk-tm-rm{width:30px;height:30px;border-radius:9px;border:none;cursor:pointer;background:#F7F4EF;color:#9CA3AF;flex:none;
  display:inline-flex;align-items:center;justify-content:center;transition:background .2s,color .2s}
.qk-tm-rm:hover{background:rgba(224,91,76,.14);color:#E05B4C}

/* ---- pending invites ---- */
.qk-tm-pend{display:flex;align-items:center;gap:12px;border:1.5px dashed #E2DCD2;border-radius:14px;padding:11px 13px;
  background:#FCFBF9;margin-bottom:8px}
.qk-tm-pend .pic{width:38px;height:38px;border-radius:11px;flex:none;display:flex;align-items:center;justify-content:center;
  background:color-mix(in srgb,var(--rc) 12%,#fff);color:var(--rc)}
.qk-tm-pend .pt{flex:1;min-width:0}
.qk-tm-pend .pr{font-size:13px;font-weight:700;color:#14201B}
.qk-tm-pend .pv{font-size:11.5px;color:#9CA3AF;margin-top:1px}
.qk-tm-pend .pc{font-size:12px;font-weight:700;color:#E05B4C;cursor:pointer;flex:none;white-space:nowrap}
"""
